import React, { useEffect, useRef, useState } from 'react';
import { peakBetween, peakHeight } from '../../../utils/editorWaveform';
import { TONE_COLORS } from '../nodeTheme';

interface ClipWaveformProps {
  peaks: Float32Array;
  /** The part of the media the clip uses, from `inSec` for `durationSec`. */
  inSec: number;
  durationSec: number;
  /** Timeline zoom. */
  pxPerSec: number;
  height: number;
  /** The track's volume: the drawing shrinks with it. */
  gain?: number;
}

/** Tiles of this many pixels, so a long clip at high zoom never needs one huge canvas. */
const TILE_PX = 2048;
const BAR_PX = 2;
/** The bars are drawn in the palette's ink, at half strength so the clip's own colour shows through. */
const COLOR = TONE_COLORS.ink.deep;
const STRENGTH = 0.5;

/**
 * A clip's sound, drawn as mirrored bars on a decibel scale behind its label. The drawing
 * is split in tiles that are only drawn once they scroll into view.
 */
export const ClipWaveform: React.FC<ClipWaveformProps> = ({ peaks, inSec, durationSec, pxPerSec, height, gain = 1 }) => {
  const width = Math.max(0, durationSec * pxPerSec);
  const tiles = Math.ceil(width / TILE_PX);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true" data-waveform>
      {Array.from({ length: tiles }, (_, i) => (
        <WaveTile
          key={i}
          peaks={peaks}
          left={i * TILE_PX}
          width={Math.min(TILE_PX, width - i * TILE_PX)}
          height={height}
          fromSec={inSec + (i * TILE_PX) / pxPerSec}
          pxPerSec={pxPerSec}
          gain={gain}
        />
      ))}
    </div>
  );
};

const WaveTile: React.FC<{ peaks: Float32Array; left: number; width: number; height: number; fromSec: number; pxPerSec: number; gain: number }> = ({
  peaks, left, width, height, fromSec, pxPerSec, gain,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || seen) return;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    // The viewport, clipped by the timeline's scrolling, tells when the tile is on screen
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) { setSeen(true); observer.disconnect(); }
    }, { rootMargin: '0px 400px' });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [seen]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !seen || width < 1 || height < 1) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = COLOR;
    ctx.globalAlpha = STRENGTH;
    const mid = canvas.height / 2;
    const step = BAR_PX * dpr;
    const bar = Math.max(1, step - dpr);
    for (let x = 0; x < canvas.width; x += step) {
      const from = fromSec + x / dpr / pxPerSec;
      const to = fromSec + (x + step) / dpr / pxPerSec;
      const half = peakHeight(peakBetween(peaks, from, to) * gain) * (mid - dpr);
      if (half <= 0) continue;
      ctx.fillRect(x, mid - half, bar, half * 2);
    }
  }, [seen, peaks, width, height, fromSec, pxPerSec, gain]);

  return <canvas ref={canvasRef} className="absolute top-0" style={{ left, width, height }} />;
};

export default ClipWaveform;
